/**
 * useThreadCompose — the "+" composer's React glue (2026-09-08, chat-thread
 * experience plan PR-1).
 *
 * Every "+" used to mint a silent empty thread (`threadStore.newThread()`),
 * and the only way to bring people in was a second affordance the owner had
 * to know about. Now every "+" opens ONE picker over the workspace roster:
 * name it (optional), choose people (or nobody), decide whether Prest0n is in,
 * and start. The hook owns the picker's state — the roster read (same loader and normalizer
 * as the add-people panel, instant-open seed included), the selection, the
 * Prest0n toggle, busy/notice — and hands the CHOICE to the host, which mints
 * the thread and performs the authorized create-with-seeds write. It never
 * writes Firestore directly and never touches the send path.
 *
 * Inert by contract: `enabled:false` (the build-time lever, or an unresolved
 * account) ⇒ `open()` is a no-op and the host falls back to the silent mint.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  deriveAddPeopleModel,
  resolveThreadAddPeopleEnabled,
  resolveThreadComposeOnNewEnabled,
  resolveThreadNameOnNewEnabled,
} from '../../services/threadAddPeople';
import type {
  AddPeopleModel,
  AddPeoplePendingInvite,
  AddPeopleRosterEntry,
} from '../../services/threadAddPeople';
import { loadWorkspaceRoster } from '../../services/threadAddPeople/workspaceRoster';
import { ADD_PEOPLE_COPY } from './threadAddPeopleCopy';

export interface ThreadComposeChoice {
  /** Uids to seed into the new thread (never includes self). */
  memberIds: string[];
  /** Prest0n in the new thread (true by default). */
  assistantEnabled: boolean;
  /**
   * The name the starter typed, or '' (2026-09-13). It becomes the thread's
   * DEFAULT name — what every member starts from — not a personal one; '' is
   * the ordinary case and leaves the thread to be named from its summary.
   */
  name: string;
}

export interface UseThreadComposeArgs {
  accountId: string | null | undefined;
  selfId: string | null | undefined;
  /** Instant-open seed — the host's mount-time roster (see useThreadAddPeople). */
  seedRoster?: AddPeopleRosterEntry[];
  /** Test seam for the build-time lever. */
  enabledOverride?: boolean;
  /** Test seam for the name field's own lever. */
  nameEnabledOverride?: boolean;
  /**
   * The host mints the thread and performs the authorized write. Resolves
   * when the thread is active; a rejection keeps the picker open with a
   * notice so the choice is never silently lost.
   */
  onStart: (choice: ThreadComposeChoice) => Promise<void> | void;
}

export interface ThreadComposeController {
  enabled: boolean;
  isOpen: boolean;
  loading: boolean;
  loadError: string | null;
  model: AddPeopleModel | null;
  filter: string;
  setFilter: (value: string) => void;
  selectedIds: ReadonlySet<string>;
  toggleSelect: (userId: string) => void;
  assistantEnabled: boolean;
  setAssistantEnabled: (next: boolean) => void;
  /**
   * The optional name for the new thread, and its setter. `nameEnabled` is the
   * build-time lever (`REACT_APP_THREAD_NAME_ON_NEW=off`); when false the host
   * withholds the field and starting a thread behaves exactly as before.
   */
  nameEnabled: boolean;
  name: string;
  setName: (value: string) => void;
  busy: boolean;
  notice: string | null;
  mismatchNotice: string | null;
  open: () => void;
  close: () => void;
  retry: () => void;
  /** Start with the current selection. */
  confirm: () => void;
  /** Start with nobody (Prest0n per the toggle). */
  justMe: () => void;
}

export function useThreadCompose(args: UseThreadComposeArgs): ThreadComposeController {
  const accountId = (args.accountId ?? '').trim();
  const selfId = (args.selfId ?? '').trim();
  const onStartRef = useRef(args.onStart);
  onStartRef.current = args.onStart;
  const seedRosterRef = useRef(args.seedRoster);
  seedRosterRef.current = args.seedRoster;

  const enabled =
    (args.enabledOverride ?? resolveThreadComposeOnNewEnabled())
    && resolveThreadAddPeopleEnabled({ accountId: accountId || null });
  const nameEnabled = enabled && (args.nameEnabledOverride ?? resolveThreadNameOnNewEnabled());

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [roster, setRoster] = useState<AddPeopleRosterEntry[]>([]);
  const rosterRef = useRef<AddPeopleRosterEntry[]>([]);
  rosterRef.current = roster;
  const [pendingInvites, setPendingInvites] = useState<AddPeoplePendingInvite[]>([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assistantEnabled, setAssistantEnabled] = useState(true);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [mismatchNotice, setMismatchNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !accountId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await loadWorkspaceRoster(accountId, {
        onPending: (pending) => setPendingInvites(pending),
      });
      if (result.error) {
        if (rosterRef.current.length === 0) {
          setRoster([]);
          setPendingInvites([]);
          setLoadError(result.error);
        }
        return;
      }
      const entries = result.entries.map((entry) => ({
        userId: entry.userId,
        displayName: entry.displayName,
        email: entry.email,
        active: entry.active,
        status: entry.status,
      }));
      rosterRef.current = entries;
      setRoster(entries);
      setMismatchNotice(result.mismatch ? ADD_PEOPLE_COPY.workspaceMismatch : null);
    } catch {
      if (rosterRef.current.length === 0) {
        setRoster([]);
        setPendingInvites([]);
        setLoadError("Couldn't load your workspace members.");
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, accountId]);

  const open = useCallback(() => {
    if (!enabled) return;
    setNotice(null);
    setLoadError(null);
    setBusy(false);
    setFilter('');
    setSelected(new Set());
    setAssistantEnabled(true);
    setName('');
    const seed = seedRosterRef.current;
    if (seed && seed.length > 0 && rosterRef.current.length === 0) {
      rosterRef.current = seed;
      setRoster(seed);
    }
    setIsOpen(true);
    void load();
  }, [enabled, load]);

  const close = useCallback(() => {
    setIsOpen(false);
    setBusy(false);
    setNotice(null);
  }, []);

  const retry = useCallback(() => {
    setNotice(null);
    setLoadError(null);
    void load();
  }, [load]);

  const toggleSelect = useCallback((userId: string) => {
    const id = (userId ?? '').trim();
    if (!id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const model = useMemo<AddPeopleModel | null>(() => {
    if (!enabled || !isOpen) return null;
    return deriveAddPeopleModel({
      roster,
      currentParticipantIds: selfId ? [selfId] : [],
      selfId,
      pendingInvites,
    });
  }, [enabled, isOpen, roster, selfId, pendingInvites]);

  const start = useCallback((memberIds: string[]) => {
    if (!enabled || busy) return;
    setBusy(true);
    setNotice(null);
    void Promise.resolve()
      .then(() => onStartRef.current({ memberIds, assistantEnabled, name: name.trim() }))
      .then(() => {
        setIsOpen(false);
        setSelected(new Set());
        setName('');
      })
      .catch((error: unknown) => {
        const message = error instanceof Error && error.message ? error.message : "Couldn't start the thread — try again.";
        setNotice(message);
      })
      .finally(() => setBusy(false));
  }, [enabled, busy, assistantEnabled, name]);

  const confirm = useCallback(() => start([...selected].filter((id) => id !== selfId)), [start, selected, selfId]);
  const justMe = useCallback(() => start([]), [start]);

  return {
    enabled,
    isOpen,
    loading,
    loadError,
    model,
    filter,
    setFilter,
    selectedIds: selected,
    toggleSelect,
    assistantEnabled,
    setAssistantEnabled,
    nameEnabled,
    name,
    setName,
    busy,
    notice,
    mismatchNotice,
    open,
    close,
    retry,
    confirm,
    justMe,
  };
}
